import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { LicensesType, TLicensePriceListDTO, TPersonalLicense } from '@/core/infra';
import { getPaymentMethods, PaymentMethods } from '@/components/ConfirmBuyingLicense/service';
import { usePersonalLicenses } from '@/src/hooks/usePersonalLicenses';
import { getPrices } from '@/components/Prices/service';
import { useContextState } from '@/components/context';

type UseDataProps = {
  type?: string;
  licenseId?: string;
  priceId?: string;
};

const useData = (query: UseDataProps) => {
  const { type, licenseId, priceId } = query;

  const router = useRouter();
  const { authorized } = useContextState();
  const { personalLicenses } = usePersonalLicenses();

  const [loading, setLoading] = useState<boolean>(true);
  const [license, setLicense] = useState<TPersonalLicense>();
  const [sameTypeLicenseId, setSameTypeLicenseId] = useState<number>();
  const [prices, setPrices] = useState<TLicensePriceListDTO>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethods[]>([]);

  const licenseType = useMemo(
    () => LicensesType[type?.toUpperCase()] ?? LicensesType.STOCK_MARKET,
    [type]
  );

  useEffect(() => {
    if (!authorized) return;

    getPaymentMethods().then(setPaymentMethods);

    getPrices().then((prices) => {
      setPrices(prices?.filter((price) => price.type === licenseType));
      setLoading(false);
    });
  }, [licenseId, type, authorized]);

  useEffect(() => {
    if (licenseId && personalLicenses.length) {
      // @ts-ignore
      const license = personalLicenses?.find((license) => license.licenseId === Number(licenseId));

      if (license && license?.licenseType !== licenseType) {
        router.push(
          `/terminal/confirm-buying-license/${licenseType.toLowerCase()}?priceId=${priceId}`
        );
      }

      setLicense(license);
    } else {
      setLicense(undefined);
    }

    if (!licenseId && personalLicenses.length) {
      // @ts-ignore
      const license = personalLicenses?.find(
        (license) =>
          license.licenseType === licenseType.toUpperCase() &&
          license.commerceType === 'PAID' &&
          license.productId !== 6
      );

      license && setSameTypeLicenseId(license.licenseId);
    } else {
      setSameTypeLicenseId(undefined);
    }
  }, [personalLicenses, licenseId]);

  return {
    prices,
    license,
    loading,
    setLoading,
    licenseType,
    paymentMethods,
    sameTypeLicenseId,
  };
};

export { useData };
